import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '../contexts/UserContext';
import { useScrollPrevention } from '../hooks/useScrollPrevention';
import { useModalBackHandler } from '../hooks/useModalBackHandler';
import { getUserInfo, updateUserInfo } from '../services/api';
import { getCities, getDistricts } from '../services/sparePartsService';
import { formatPhoneNumber, cleanPhoneNumber, isValidTurkishMobile, isValidTurkishPhone, handlePhoneKeyDown } from '../utils/phoneUtils';
import { normalizeEmail, isValidEmail } from '../utils/emailUtils';
import LocationSelectionModal from './LocationSelectionModal';
import AnimatedCloseButton from './AnimatedCloseButton';

interface UserInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (error: string) => void;
  onDataLoaded?: () => void; // Modal veriler yüklendiğinde çağrılır
  onModalOpened?: () => void; // Modal açıldığında çağrılır
  showBackdrop?: boolean; // Backdrop gösterilsin mi?
}

interface UserData {
  KullaniciAdi: string;
  Ad: string;
  Adres: string;
  PostaKodu: string;
  Telefon1: string;
  Telefon2: string;
  Faks: string;
  EPosta: string;
  WebSitesi: string;
  Il?: number;
  Ilce?: number;
  Ulke?: number;
}

interface City {
  Id: number;
  Ad: string;
}

interface District {
  Id: number;
  Ad: string;
}

export default function UserInfoModal({ isOpen, onClose, onSuccess, onError, onDataLoaded, onModalOpened, showBackdrop = true }: UserInfoModalProps) {
  const { userData } = useUser();

  // Internal modal state - veriler yüklendikten sonra true olur
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const [userInfo, setUserInfo] = useState<UserData>({
    KullaniciAdi: '',
    Ad: '',
    Adres: '',
    PostaKodu: '',
    Telefon1: '',
    Telefon2: '',
    Faks: '',
    EPosta: '',
    WebSitesi: '',
    Il: undefined,
    Ilce: undefined,
    Ulke: undefined
  });

  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  // Scroll prevention Account.tsx'te loading overlay ile yönetiliyor
  // Burada scroll prevention yapmıyoruz çünkü çakışma yaratıyor

  // İl ve İlçe states
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<District | null>(null);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  // Modal states
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [isDistrictModalOpen, setIsDistrictModalOpen] = useState(false);

  // Modal geri düğmesi handler - alt modal'lar açıkken devre dışı
  useModalBackHandler({ 
    isOpen: internalIsOpen, 
    onClose, 
    enabled: !isCityModalOpen && !isDistrictModalOpen
  });

  // Search states
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [districtSearchTerm, setDistrictSearchTerm] = useState('');

  // API functions
  const fetchCities = useCallback(async (countryId: number) => {
    setLoadingCities(true);
    try {
      const data = await getCities(countryId);
      const cityList = data.data || [];
      setCities(cityList);
    } catch (error) {
      console.error('İl listesi yüklenirken hata:', error);
      onError('İl listesi yüklenirken bir hata oluştu.');
    } finally {
      setLoadingCities(false);
    }
  }, [onError]);

  const fetchDistricts = useCallback(async (cityId: number) => {
    setLoadingDistricts(true);
    try {
      const data = await getDistricts(cityId);
      setDistricts(data.data || []);
    } catch (error) {
      console.error('İlçe listesi yüklenirken hata:', error);
      onError('İlçe listesi yüklenirken bir hata oluştu.');
    } finally {
      setLoadingDistricts(false);
    }
  }, [onError]);

  // Modal açılma isteği geldiğinde verileri yükle
  useEffect(() => {
    if (isOpen && !dataLoading && !internalIsOpen) {
      loadUserData();
    } else if (!isOpen && internalIsOpen) {
      // Modal kapatılma isteği geldiğinde animasyon için internal modal'i kapat
      setInternalIsOpen(false);
    } else if (!isOpen && !internalIsOpen) {
      // Modal tamamen kapandıktan sonra state'leri temizle
      setUserInfo({
        KullaniciAdi: '',
        Ad: '',
        Adres: '',
        PostaKodu: '',
        Telefon1: '',
        Telefon2: '',
        Faks: '',
        EPosta: '',
        WebSitesi: '',
        Il: undefined,
        Ilce: undefined,
        Ulke: undefined
      });
      setErrors({});
      setLoading(false);
      setDataLoading(false);
      setSelectedCity(null);
      setSelectedDistrict(null);
      setCities([]);
      setDistricts([]);
      setCitySearchTerm('');
      setDistrictSearchTerm('');
      setIsCityModalOpen(false);
      setIsDistrictModalOpen(false);
    }
  }, [isOpen, dataLoading, internalIsOpen]);

  const loadUserData = async () => {
    if (!userData?.User) {
      onError('Kullanıcı bilgileri bulunamadı.');
      return;
    }

    setDataLoading(true);
    const loadingStartTime = Date.now(); // Loading başlangıç zamanı
    
    try {
      const result = await getUserInfo(userData.User);
      if (result.success && result.data) {
        const data = result.data;
        setUserInfo({
          KullaniciAdi: data.KullaniciAdi || '',
          Ad: data.Ad || '',
          Adres: data.Adres || '',
          PostaKodu: data.PostaKodu || '',
          Telefon1: formatPhoneNumber(data.Telefon1 || ''),
          Telefon2: formatPhoneNumber(data.Telefon2 || ''),
          Faks: formatPhoneNumber(data.Faks || ''),
          EPosta: normalizeEmail(data.EPosta || ''),
          WebSitesi: data.WebSitesi || '',
          Il: data.Il,
          Ilce: data.Ilce,
          Ulke: data.Ulke
        });

        // Türkiye için il/ilçe bilgilerini yükle
        if (data.Ulke === 1) {
          await fetchCities(1);
          
          // Mevcut il bilgisi varsa ilçeleri de yükle
          if (data.Il) {
            await fetchDistricts(data.Il);
          }
        }
      } else {
        onError(result.error || 'Kullanıcı bilgileri yüklenemedi.');
      }
    } catch (error) {
      onError('Kullanıcı bilgileri yüklenirken bir hata oluştu.');
    } finally {
      setDataLoading(false);
      
      // Veriler yüklendikten sonra modal'i aç
      if (isOpen) {
        // Modal'i hemen aç
        setInternalIsOpen(true);
        // Parent'a modal açıldığını bildir (spinner'ı gizlemek için)
        onModalOpened?.();
        // onDataLoaded çağrılmayacak - loading overlay modal kapanana kadar devam edecek
      }
    }
  };

  // İller yüklendiğinde mevcut il'i seç
  useEffect(() => {
    if (cities.length > 0 && userInfo.Il && !selectedCity) {
      const city = cities.find(c => c.Id === userInfo.Il);
      if (city) {
        setSelectedCity(city);
        if (userInfo.Ilce) {
          fetchDistricts(city.Id);
        }
      }
    }
  }, [cities, userInfo.Il, selectedCity, userInfo.Ilce, fetchDistricts]);

  // İlçeler yüklendiğinde mevcut ilçe'yi seç
  useEffect(() => {
    if (districts.length > 0 && userInfo.Ilce && !selectedDistrict) {
      const district = districts.find(d => d.Id === userInfo.Ilce);
      if (district) {
        setSelectedDistrict(district);
      }
    }
  }, [districts, userInfo.Ilce, selectedDistrict]);

  const validateForm = () => {
    const newErrors: { [key: string]: string | undefined } = {};
    let errorMessage = '';

    // Kullanıcı adı kontrolü
    if (!userInfo.KullaniciAdi.trim()) {
      newErrors.KullaniciAdi = 'error';
      errorMessage = 'Kullanıcı adını giriniz.';
    }

    // Ad kontrolü
    if (!userInfo.Ad.trim()) {
      newErrors.Ad = 'error';
      if (!errorMessage) errorMessage = 'Firma adını giriniz.';
    }

    // Telefon kontrolü
    if (!userInfo.Telefon1.trim()) {
      newErrors.Telefon1 = 'error';
      if (!errorMessage) errorMessage = 'Telefon numarasını giriniz.';
    } else if (!isValidTurkishMobile(userInfo.Telefon1)) {
      newErrors.Telefon1 = 'error';
      if (!errorMessage) errorMessage = 'Geçerli bir telefon numarası giriniz.';
    }

    // Telefon2 format kontrolü (zorunlu değil ama girilmişse geçerli olmalı - hem mobil hem sabit hat)
    if (userInfo.Telefon2.trim() && !isValidTurkishPhone(userInfo.Telefon2)) {
      newErrors.Telefon2 = 'error';
      if (!errorMessage) errorMessage = 'İkinci telefon numarası geçerli değil.';
    }

    // Faks format kontrolü (zorunlu değil ama girilmişse geçerli olmalı - hem mobil hem sabit hat)
    if (userInfo.Faks.trim() && !isValidTurkishPhone(userInfo.Faks)) {
      newErrors.Faks = 'error';
      if (!errorMessage) errorMessage = 'Faks numarası geçerli değil.';
    }

    // Posta kodu format kontrolü (zorunlu değil ama girilmişse geçerli olmalı)
    if (userInfo.PostaKodu.trim()) {
      const postaKodu = userInfo.PostaKodu.trim();
      if (!/^\d{5}$/.test(postaKodu) || parseInt(postaKodu) < 1000 || parseInt(postaKodu) > 99999) {
        newErrors.PostaKodu = 'error';
        if (!errorMessage) errorMessage = 'Posta kodu 5 haneli bir sayı olmalıdır (01000-99999).';
      }
    }

    // E-posta format kontrolü (zorunlu değil ama girilmişse geçerli olmalı)
    if (userInfo.EPosta.trim() && !isValidEmail(userInfo.EPosta)) {
      newErrors.EPosta = 'error';
      if (!errorMessage) errorMessage = 'Geçerli bir e-posta adresi giriniz.';
    }

    setErrors(newErrors);
    
    // Hata varsa toast göster
    if (Object.keys(newErrors).length > 0) {
      onError(errorMessage);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      if (!userData?.User) {
        onError('Kullanıcı bilgileri bulunamadı.');
        return;
      }

      setLoading(true);

      try {
        // API için veri hazırla
        const updateData = {
          KullaniciAdi: userInfo.KullaniciAdi,
          Ad: userInfo.Ad,
          Adres: userInfo.Adres,
          PostaKodu: userInfo.PostaKodu,
          Telefon1: cleanPhoneNumber(userInfo.Telefon1),
          Telefon2: cleanPhoneNumber(userInfo.Telefon2),
          Faks: cleanPhoneNumber(userInfo.Faks),
          EPosta: userInfo.EPosta,
          WebSitesi: userInfo.WebSitesi,
          Il: selectedCity?.Id || userInfo.Il,
          Ilce: selectedDistrict?.Id || userInfo.Ilce,
          Ulke: userInfo.Ulke || 1 // Türkiye
        };

        const result = await updateUserInfo(updateData, userData.User);

        if (result.success) {
          onSuccess('Kullanıcı bilgileriniz başarıyla güncellendi.');
          handleClose();
        } else {
          onError(result.error || 'Kullanıcı bilgileri güncellenemedi.');
        }
      } catch (error) {
        onError('Kullanıcı bilgileri güncellenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    // Loading overlay'i kapat (eğer hala açıksa)
    onDataLoaded?.();
    // Parent'a kapatma isteği gönder - parent false yaparsa useEffect ile internal modal kapanacak
    onClose();
  };

  const handleInputChange = (field: keyof UserData, value: string | number | undefined) => {
    setUserInfo(prev => ({
      ...prev,
      [field]: value
    }));

    // Kullanıcı yazmaya başladığında sadece o alanın hatasını temizle
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Telefon numarası için özel handler
  const handlePhoneChange = (field: 'Telefon1' | 'Telefon2' | 'Faks') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    handleInputChange(field, formatted);
  };

  // E-posta için özel handler
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = normalizeEmail(e.target.value);
    handleInputChange('EPosta', normalized);
  };

  // Posta kodu için özel handler (sadece sayı)
  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, ''); // Sadece sayıları al
    if (value.length <= 5) { // 5 haneli limit
      handleInputChange('PostaKodu', value);
    }
  };

  // Telefon alanında backspace işlemini yönet
  const phoneKeyDownHandler = useCallback((field: 'Telefon1' | 'Telefon2' | 'Faks') => (e: React.KeyboardEvent<HTMLInputElement>) => {
    const setPhoneNumber = (value: string) => handleInputChange(field, value);
    const phoneErrorState = !!errors[field];
    const setPhoneErrorState = (value: boolean) => {
      if (!value) {
        setErrors(prev => ({ ...prev, [field]: undefined }));
      }
    };

    handlePhoneKeyDown(e, setPhoneNumber, !!Object.keys(errors).length, phoneErrorState, setPhoneErrorState);
  }, [errors]);

  // İl ve İlçe handler'ları
  const handleCitySelect = (city: City | null) => {
    if (city) {
      setSelectedCity(city);
      setSelectedDistrict(null);
      handleInputChange('Il', city.Id);
      handleInputChange('Ilce', undefined);
      setDistricts([]);
      fetchDistricts(city.Id);
      setIsCityModalOpen(false);
      setCitySearchTerm('');

      if (errors.Il) {
        setErrors(prev => ({ ...prev, Il: undefined }));
      }
    }
  };

  const handleDistrictSelect = (district: District | null) => {
    if (district) {
      setSelectedDistrict(district);
      handleInputChange('Ilce', district.Id);
      setIsDistrictModalOpen(false);
      setDistrictSearchTerm('');

      if (errors.Ilce) {
        setErrors(prev => ({ ...prev, Ilce: undefined }));
      }
    }
  };

  // Arama fonksiyonları
  const handleCitySearchChange = (term: string) => {
    setCitySearchTerm(term);
  };

  const handleDistrictSearchChange = (term: string) => {
    setDistrictSearchTerm(term);
  };



  // Modal içeriğini render et (loading state dahil)

  return (
    <>
      <AnimatePresence>
        {internalIsOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 overflow-y-auto"
          >
            {/* Modal */}
            <div className="flex min-h-full items-center justify-center px-2 py-4 text-center sm:p-0">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative transform transition-transform overflow-visible rounded-xl backdrop-blur-xs bg-gradient-to-br from-white/90 via-white/88 to-white/85 dark:from-gray-700/85 dark:via-gray-750/83 dark:to-gray-800/85 border border-white/50 dark:border-gray-600/50 text-left shadow-2xl shadow-black/10 dark:shadow-black/30 w-full mx-2 sm:mx-0 sm:my-8 sm:w-full sm:max-w-2xl"
                role="dialog"
                aria-modal="true"
              >
                {/* Header */}
                <div className="border-b border-gray-300 dark:border-gray-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Kullanıcı Bilgileri
                    </h3>
                    <AnimatedCloseButton 
                      onClick={handleClose}
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Content */}
                <form onSubmit={handleSubmit} className="px-6 pt-4 pb-5 sm:p-8 overflow-visible modal-scrollable">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-visible">
                    {/* Kullanıcı Adı */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Kullanıcı Adı <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={userInfo.KullaniciAdi}
                        onChange={(e) => handleInputChange('KullaniciAdi', e.target.value)}
                        maxLength={50}
                        className={errors.KullaniciAdi ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                        placeholder="Kullanıcı adınızı girin"
                      />
                    </div>

                    {/* Ülke */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Ülke
                      </label>
                      <input
                        type="text"
                        value="Türkiye"
                        readOnly
                        className="form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 cursor-not-allowed"
                        placeholder="Ülke"
                      />
                    </div>

                    {/* Firma Adı */}
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Firma Adı <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={userInfo.Ad}
                        onChange={(e) => handleInputChange('Ad', e.target.value)}
                        maxLength={200}
                        className={errors.Ad ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                        placeholder="Firma adınızı girin"
                      />
                    </div>

                    {/* Telefon 1 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Telefon <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={userInfo.Telefon1}
                        onChange={handlePhoneChange('Telefon1')}
                        onKeyDown={phoneKeyDownHandler('Telefon1')}
                        maxLength={17}
                        className={errors.Telefon1 ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                        placeholder="Telefon numaranızı girin"
                      />
                    </div>

                    {/* Telefon 2 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Telefon 2
                      </label>
                      <input
                        type="tel"
                        value={userInfo.Telefon2}
                        onChange={handlePhoneChange('Telefon2')}
                        onKeyDown={phoneKeyDownHandler('Telefon2')}
                        maxLength={20}
                        className={errors.Telefon2 ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                        placeholder="İkinci telefon numarası (isteğe bağlı)"
                      />
                    </div>

                    {/* Faks */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Faks
                      </label>
                      <input
                        type="tel"
                        value={userInfo.Faks}
                        onChange={handlePhoneChange('Faks')}
                        onKeyDown={phoneKeyDownHandler('Faks')}
                        maxLength={20}
                        className={errors.Faks ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                        placeholder="Faks numarası (isteğe bağlı)"
                      />
                    </div>

                    {/* Posta Kodu */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Posta Kodu
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={userInfo.PostaKodu}
                        onChange={handlePostalCodeChange}
                        maxLength={5}
                        className={errors.PostaKodu ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                        placeholder="Posta kodu (isteğe bağlı)"
                      />
                    </div>

                    {/* İl Seçimi */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        İl
                      </label>
                      <div className="relative">
                        <div
                          onClick={() => setIsCityModalOpen(true)}
                          tabIndex={0}
                          className={`${errors.Il ? 'form-combobox-error' : 'form-combobox'} w-full relative cursor-pointer bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 hover:border-gray-400 dark:hover:border-gray-500`}
                        >
                          <span className={selectedCity ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
                            {selectedCity ? selectedCity.Ad : 'İl seçin'}
                          </span>
                          <svg className={`h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isCityModalOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* İlçe Seçimi */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        İlçe
                      </label>
                      <div className="relative">
                        <div
                          onClick={() => selectedCity && setIsDistrictModalOpen(true)}
                          tabIndex={selectedCity ? 0 : -1}
                          className={`${errors.Ilce ? 'form-combobox-error' : 'form-combobox'} w-full relative ${selectedCity ? 'cursor-pointer bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 hover:border-gray-400 dark:hover:border-gray-500' : 'cursor-not-allowed opacity-50 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2'}`}
                        >
                          <span className={selectedDistrict ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
                            {selectedDistrict ? selectedDistrict.Ad : 'İlçe seçin'}
                          </span>
                          <svg className={`h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isDistrictModalOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* E-posta */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        E-Posta
                      </label>
                      <input
                        type="email"
                        value={userInfo.EPosta}
                        onChange={handleEmailChange}
                        maxLength={100}
                        className={errors.EPosta ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                        placeholder="E-posta adresi (isteğe bağlı)"
                      />
                    </div>

                    {/* Web Sitesi */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Web Sitesi
                      </label>
                      <input
                        type="url"
                        value={userInfo.WebSitesi}
                        onChange={(e) => handleInputChange('WebSitesi', e.target.value)}
                        maxLength={100}
                        className={errors.WebSitesi ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                        placeholder="Web sitesi adresi (isteğe bağlı)"
                      />
                    </div>
                  </div>

                  {/* Adres - Full width */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Adres
                    </label>
                    <textarea
                      value={userInfo.Adres}
                      onChange={(e) => handleInputChange('Adres', e.target.value)}
                      rows={3}
                      maxLength={200}
                      className={errors.Adres ? 'form-textarea-error w-full' : 'form-textarea w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                      placeholder="Adres bilgisi (isteğe bağlı)"
                    />
                                      </div>
                </form>

                {/* Footer */}
                <div className="border-t border-gray-300 dark:border-gray-600 px-6 py-5">
                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={loading}
                      className="border border-gray-300 dark:border-gray-600 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                    >
                      <div className="rounded-lg border border-white dark:border-gray-700 px-5 py-2 text-base font-semibold text-gray-700 dark:text-gray-300">
                        İptal
                      </div>
                    </button>
                    <button
                      type="submit"
                      onClick={handleSubmit}
                      disabled={loading}
                      className="px-5 py-2 text-base font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-lg shadow-lg shadow-blue-500/25 transform transition-all duration-200 hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/30 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700"
                    >
                      {loading && (
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      Güncelle
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* İl Seçim Modal'ı */}
      <LocationSelectionModal
        isOpen={isCityModalOpen}
        onClose={() => setIsCityModalOpen(false)}
        onSelect={handleCitySelect}
        items={cities}
        selectedId={selectedCity?.Id}
        loading={loadingCities}
        title="İl Seçin"
        searchPlaceholder="İl ara..."
        emptyMessage="Uygun il bulunamadı."
        searchTerm={citySearchTerm}
        onSearchChange={handleCitySearchChange}
        isRequired={false}
        formSubmitted={!!errors.Il}
        error={!!errors.Il}
      />

      {/* İlçe Seçim Modal'ı */}
      <LocationSelectionModal
        isOpen={isDistrictModalOpen}
        onClose={() => setIsDistrictModalOpen(false)}
        onSelect={handleDistrictSelect}
        items={districts}
        selectedId={selectedDistrict?.Id}
        loading={loadingDistricts}
        title="İlçe Seçin"
        searchPlaceholder="İlçe ara..."
        emptyMessage={selectedCity ? 'Uygun ilçe bulunamadı.' : 'Önce il seçin.'}
        searchTerm={districtSearchTerm}
        onSearchChange={handleDistrictSearchChange}
        isRequired={false}
        formSubmitted={!!errors.Ilce}
        error={!!errors.Ilce}
      />
    </>
  );
}
